import React, { useState } from 'react';
import { AlertsPanel, CompactAlertsPanel } from '@/components/AlertsPanel';
import type { AlertItem } from '@/components/AlertsPanel';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Plus, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

// Sample alerts data
const sampleAlerts: AlertItem[] = [
  {
    id: '1',
    type: 'error',
    priority: 'high',
    title: 'Timesheet Submission Failed',
    message: 'Your timesheet for week ending 22/12/2024 could not be submitted. Please check your entries and try again.',
    timestamp: new Date(Date.now() - 1000 * 60 * 15), // 15 minutes ago
    actionLabel: 'Review Timesheet',
    onAction: () => toast.info('Navigating to timesheet...'),
    dismissible: true,
  },
  {
    id: '2',
    type: 'warning',
    priority: 'high',
    title: 'Excessive Hours Detected',
    message: 'You have logged 12.00 hours on Monday. Please ensure this is accurate and approved.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 hours ago
    actionLabel: 'Edit Entry',
    onAction: () => toast.info('Opening editor...'),
    dismissible: true,
  },
  {
    id: '3',
    type: 'warning',
    priority: 'medium',
    title: 'Weekend Work Logged',
    message: 'You have logged hours on Saturday and Sunday. Ensure this has been pre-approved by your manager.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 5), // 5 hours ago
    dismissible: true,
  },
  {
    id: '4',
    type: 'warning',
    priority: 'medium',
    title: 'Missing Descriptions',
    message: '3 timesheet entries are missing descriptions. Please add descriptions before submitting.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 8), // 8 hours ago
    actionLabel: 'Add Descriptions',
    onAction: () => toast.info('Opening entries...'),
    dismissible: true,
  },
  {
    id: '5',
    type: 'info',
    priority: 'low',
    title: 'New Project Available',
    message: 'Project "Cloud Migration - Phase 3" is now available for time tracking.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24), // 1 day ago
    actionLabel: 'View Project',
    onAction: () => toast.info('Opening project details...'),
    dismissible: true,
  },
  {
    id: '6',
    type: 'info',
    priority: 'low',
    title: 'Reminder: Submit Timesheet',
    message: 'Your timesheet for this week is due by Friday 5:00 PM.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2), // 2 days ago
    dismissible: true,
  },
  {
    id: '7',
    type: 'success',
    priority: 'low',
    title: 'Timesheet Approved',
    message: 'Your timesheet for week ending 15/12/2024 has been approved by John Smith.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3), // 3 days ago
    dismissible: true,
  },
  {
    id: '8',
    type: 'success',
    priority: 'low',
    title: 'Profile Updated',
    message: 'Your profile information has been successfully updated.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5), // 5 days ago
    dismissible: true,
  },
];

export function AlertsDemoPage() {
  const [alerts, setAlerts] = useState<AlertItem[]>(sampleAlerts);
  const [showCompact, setShowCompact] = useState(false);

  const handleDismiss = (id: string) => {
    setAlerts(alerts.filter(alert => alert.id !== id));
    toast.success('Alert dismissed');
  };

  const addRandomAlert = () => {
    const types: Array<'error' | 'warning' | 'info' | 'success'> = ['error', 'warning', 'info', 'success'];
    const priorities: Array<'high' | 'medium' | 'low'> = ['high', 'medium', 'low'];
    const randomType = types[Math.floor(Math.random() * types.length)];
    const randomPriority = priorities[Math.floor(Math.random() * priorities.length)];

    const newAlert: AlertItem = {
      id: `alert-${Date.now()}`,
      type: randomType,
      priority: randomPriority,
      title: `New ${randomType.charAt(0).toUpperCase() + randomType.slice(1)} Alert`,
      message: 'This is a dynamically generated alert to demonstrate the panel functionality.',
      timestamp: new Date(),
      dismissible: true,
    };

    setAlerts([newAlert, ...alerts]);
    toast.success('New alert added');
  };

  const resetAlerts = () => {
    setAlerts(sampleAlerts);
    toast.success('Alerts reset to default');
  };

  const clearAllAlerts = () => {
    setAlerts([]);
    toast.success('All alerts cleared');
  };

  const stats = {
    total: alerts.length,
    errors: alerts.filter(a => a.type === 'error').length,
    warnings: alerts.filter(a => a.type === 'warning').length,
    info: alerts.filter(a => a.type === 'info').length,
    success: alerts.filter(a => a.type === 'success').length,
    highPriority: alerts.filter(a => a.priority === 'high').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Enhanced Alerts Panel</h1>
        <p className="text-muted-foreground mt-2">
          A visually appealing and functional alerts system with grouping, priorities, and animations
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">Total Alerts</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50 dark:bg-red-950/20">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-600">{stats.errors}</div>
            <p className="text-xs text-red-900 dark:text-red-100">Errors</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-amber-600">{stats.warnings}</div>
            <p className="text-xs text-amber-900 dark:text-amber-100">Warnings</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/20">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.info}</div>
            <p className="text-xs text-blue-900 dark:text-blue-100">Info</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50 dark:bg-green-950/20">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">{stats.success}</div>
            <p className="text-xs text-green-900 dark:text-green-100">Success</p>
          </CardContent>
        </Card>
        <Card className="border-2 border-red-300">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-600">{stats.highPriority}</div>
            <p className="text-xs text-muted-foreground">High Priority</p>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Demo Controls</CardTitle>
          <CardDescription>
            Test the alerts panel functionality with these controls
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button onClick={addRandomAlert} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Random Alert
            </Button>
            <Button onClick={resetAlerts} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset to Default
            </Button>
            <Button onClick={clearAllAlerts} variant="outline" size="sm">
              <Trash2 className="h-4 w-4 mr-2" />
              Clear All
            </Button>
            <div className="flex-1" />
            <Button
              onClick={() => setShowCompact(!showCompact)}
              variant={showCompact ? 'default' : 'outline'}
              size="sm"
            >
              {showCompact ? 'Full View' : 'Compact View'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Alerts Panel Tabs */}
      <Tabs defaultValue="grouped" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="grouped">Grouped & Collapsible</TabsTrigger>
          <TabsTrigger value="flat">Flat View</TabsTrigger>
          <TabsTrigger value="compact">Compact Inline</TabsTrigger>
        </TabsList>

        <TabsContent value="grouped" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Grouped & Collapsible Alerts</CardTitle>
              <CardDescription>
                Alerts are grouped by type with collapsible sections for better organization
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AlertsPanel
                alerts={alerts}
                onDismiss={handleDismiss}
                showTimestamp={true}
                groupByType={true}
                collapsible={true}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="flat" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Flat View</CardTitle>
              <CardDescription>
                All alerts displayed in a single list, sorted by priority
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AlertsPanel
                alerts={alerts}
                onDismiss={handleDismiss}
                showTimestamp={true}
                groupByType={false}
                collapsible={false}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="compact" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Compact Inline View</CardTitle>
              <CardDescription>
                Space-efficient view perfect for sidebars or inline notifications
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CompactAlertsPanel
                alerts={alerts}
                onDismiss={handleDismiss}
                maxVisible={5}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Features Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Key Features</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2">
                <Badge>1</Badge> Visual Hierarchy
              </h3>
              <p className="text-sm text-muted-foreground">
                Clear visual distinction between alert types with color-coded backgrounds, borders, and icons
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2">
                <Badge>2</Badge> Priority System
              </h3>
              <p className="text-sm text-muted-foreground">
                High, medium, and low priority badges help users focus on critical alerts first
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2">
                <Badge>3</Badge> Smart Grouping
              </h3>
              <p className="text-sm text-muted-foreground">
                Alerts grouped by type (errors, warnings, info, success) with collapsible sections
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2">
                <Badge>4</Badge> Smooth Animations
              </h3>
              <p className="text-sm text-muted-foreground">
                Framer Motion animations for alert appearance, dismissal, and group expansion
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2">
                <Badge>5</Badge> Actionable Alerts
              </h3>
              <p className="text-sm text-muted-foreground">
                Optional action buttons allow users to respond directly from the alert
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2">
                <Badge>6</Badge> Timestamps
              </h3>
              <p className="text-sm text-muted-foreground">
                Relative timestamps (e.g., "2h ago") help users understand alert recency
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2">
                <Badge>7</Badge> Responsive Design
              </h3>
              <p className="text-sm text-muted-foreground">
                Fully responsive layout that adapts to mobile, tablet, and desktop screens
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2">
                <Badge>8</Badge> Accessibility
              </h3>
              <p className="text-sm text-muted-foreground">
                WCAG compliant colors, proper ARIA labels, and keyboard navigation support
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Design Specifications */}
      <Card>
        <CardHeader>
          <CardTitle>Design Specifications</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">Color Palette</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <div className="h-12 rounded-lg bg-red-100 dark:bg-red-950/20 border-2 border-red-200 dark:border-red-800" />
                  <p className="text-xs font-medium">Error</p>
                  <p className="text-xs text-muted-foreground">Red palette</p>
                </div>
                <div className="space-y-1">
                  <div className="h-12 rounded-lg bg-amber-100 dark:bg-amber-950/20 border-2 border-amber-200 dark:border-amber-800" />
                  <p className="text-xs font-medium">Warning</p>
                  <p className="text-xs text-muted-foreground">Amber palette</p>
                </div>
                <div className="space-y-1">
                  <div className="h-12 rounded-lg bg-blue-100 dark:bg-blue-950/20 border-2 border-blue-200 dark:border-blue-800" />
                  <p className="text-xs font-medium">Info</p>
                  <p className="text-xs text-muted-foreground">Blue palette</p>
                </div>
                <div className="space-y-1">
                  <div className="h-12 rounded-lg bg-green-100 dark:bg-green-950/20 border-2 border-green-200 dark:border-green-800" />
                  <p className="text-xs font-medium">Success</p>
                  <p className="text-xs text-muted-foreground">Green palette</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Typography</h3>
              <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
                <p className="text-base font-semibold">Alert Title - Semibold 14-16px</p>
                <p className="text-sm">Alert Message - Regular 14px</p>
                <p className="text-xs opacity-70">Timestamp - Regular 12px</p>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Spacing & Layout</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>Alert padding: 16px (1rem)</li>
                <li>Gap between alerts: 8px (0.5rem)</li>
                <li>Icon size: 20px (1.25rem)</li>
                <li>Border radius: 8px (0.5rem)</li>
                <li>Border width: 2px</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
